// Spawn / placement / play-gating, extracted from GameBoard.jsx.
// spawnSiteAtCell and spawnTokenAtPoint are the single funnels for
// materialising catalog cards onto the field (both route through the
// pure buildSpawnInstance recipe); the summon-placement queue drives
// the click-to-place UX for token summons and pile spawns. The play
// gates (canPlayFromHand, canPlaceSiteAt, mandatory-action checks)
// decide what a player may legally drop right now.
//
// Legality math is pure (engine/core: placementChoicesForPlay,
// getLegalSitePlacement, spawnableCells*); this module translates the
// results into toasts, highlights, and staged-drag vetoes.

import { TRIGGER, buildSpawnInstance, cellsInScope, isDisabled, ownerElementAllowsCast } from '../../../utils/game/engine/automation';
import { bannedSiteCellsFor } from './boardLegality';
import { siteGrantInfo } from './automationRuntime';
import { siteMatchesRestriction } from '../../../utils/game/engine/automation/ops/siteRestriction';
import { collectThresholdWaivers, thresholdWaiverCellsFor } from '../../../utils/game/engine/automation/ops/thresholdWaiver';
import { checkCastLock, classifyCellForSite, formatRefusalsSentence, getAffordability, getLegalSitePlacement, isAvatarInVoid, ownerCanCastLocked, sampleCellGeometry, siteSnapPoint } from '../../../utils/game/engine/core';
import { computeGridPositions, getCellNumber } from '../../../utils/game/grid';
import { playSound } from '../../../utils/game/sounds';
import { getGameGrid } from '../../../utils/game/spawnConfig';
import { toast } from 'sonner';
import { k, t } from '../../../localization/t';
import { cardLogLabel } from './logbookChat';
import { msg } from '../../../utils/game/logMessage';
import { spawnTokenIntent } from '../../../utils/game/net/manualIntents';

// Token lookup goes through `sorcery-cards.json`. The same source
// the manual creature-token spawner (`buildTokenPile`) walks. Every
// summonable token (Skeleton, Foot Soldier, Ghoul, Hellhounds,
// Rubble, Bear Pelt summons, etc.) ships there as a zero-cost
// Minion / Artifact. Matching is case-insensitive and prefers an
// exact name; "Foot Soldier" picks the first variant when the
// catalog ships numbered variants ("Foot Soldier 1" …).
export function findCardCatalogToken(host, tokenName) {
  const want = String(tokenName || '').toLowerCase();
  if (!want) return null;
  const cards = host.props.sorceryCards || [];
  const summonable = cards.filter((c) =>
    c.cost === '' && (c.type === 'Minion' || c.type === 'Artifact')
  );
  return summonable.find((c) => (c.name || '').toLowerCase() === want)
    || summonable.find((c) => (c.name || '').toLowerCase().startsWith(want))
    || null;
}

// Spawn a Token at a world point. Single recipe shared with every
// other catalog-spawn path. See `_spawnSiteAtCell` for sites and
// `buildSpawnInstance` in `engine/automation/ops/spawn.js` for the
// pure instance builder.
export function spawnTokenAtPoint(host, cardRow, controllerRotated, point) {
  if (!cardRow || !point) return null;
  const instance = buildSpawnInstance(cardRow, {
    kind: 'token',
    rotated: controllerRotated,
    x: point.x,
    z: point.z,
  });
  if (!instance) return null;
  host.addCardToTable(instance, true, { logVerb: k('Summoned') });
  return instance;
}

// Spawn a Site at a grid cell. Funnels every site-spawn caller
// (Geomancer earth bonus, Geomancer atlas-replace, turnToRubble,
// and future avatars) through one recipe so any post-spawn pipeline
// change lands in one place.
//
// `opts.replacingSite`. Optional existing site instance to remove
//                         BEFORE the new spawn (atlas-replace path).
//                         Sent to `opts.replacingDest` ('Banished' or
//                         'Cemetery').
// `opts.fireSiteEntry`. When true, runs `applySitePlayEffects` and
//                         `_fireRealmTrigger(ON_REALM_ENTRY)` so the
//                         new site participates in affinity grants
//                         and other cards' realm-entry watchers.
//                         Rubble spawns leave this false (Rubble has
//                         no automation and nothing watches it).
export function spawnSiteAtCell(host, cardRow, cell, opts = {}) {
  const {
    rotated = false,
    replacingSite = null,
    replacingDest = 'Banished',
    fireSiteEntry = false,
    logLine = null,
  } = opts;
  if (!cardRow || !cell) return null;
  const grid = getGameGrid(host.state.spawnConfig);
  const geo = grid && sampleCellGeometry(grid, cell.col, cell.row);
  const snap = geo && siteSnapPoint(geo, !!rotated);
  if (!snap) return null;

  if (replacingSite) host.sendCardToPile(replacingSite, replacingDest);

  const instance = buildSpawnInstance(cardRow, {
    kind: 'site',
    rotated,
    x: snap.x,
    z: snap.z,
  });
  if (!instance) return null;
  // Silent add: addCardToTable's own play pipeline (log +
  // applySitePlayEffects + ON_REALM_ENTRY) would otherwise fire here
  // AND again below, double-triggering Genesis (Valley of Delight
  // granted its chosen threshold twice). spawnSiteAtCell owns the
  // post-spawn pipeline exactly once. The board recompute in
  // applySitePlayEffects runs for every spawn (a new site or Rubble
  // shifts affinities + aura coverage); realm-entry watchers fire only
  // for real site entries, never inert Rubble.
  host.addCardToTable(instance, true, { silent: true });
  if (logLine) host.logAction(logLine);
  host.applySitePlayEffects(instance);
  if (fireSiteEntry) {
    host._fireRealmTrigger(TRIGGER.ON_REALM_ENTRY, instance);
  }
  return instance;
}

// v1.15e / v1.88: enqueue N click-to-place summons of a named token.
// Replaces the prior auto-placement loop. Per user request, the
// player picks each spawn cell individually so we never auto-pick a
// bad location. The cursor shows a transparent token preview while
// the queue is non-empty (TokenSpawnPreview).
export function summonTokenAtLocations(host, sourceCard, controller, args, casterOverrides = null) {
  const tokenName = args.tokenName;
  if (!tokenName) return;
  const tokenRow = host._findCardCatalogToken(tokenName);
  if (!tokenRow) {
    toast.message(t('No "{name}" token found in catalog. Manual spawn required.', { name: tokenName }));
    return;
  }

  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return;
  const sourceMesh = host.meshes.get(sourceCard.id);
  let sourcePos;
  if (casterOverrides?.sourcePos) sourcePos = casterOverrides.sourcePos;
  else if (sourceMesh) sourcePos = { x: sourceMesh.position.x, z: sourceMesh.position.z };
  else return;
  const sourceCell = host._cellAtWorld(sourcePos.x, sourcePos.z);
  if (!sourceCell) return;

  const { colPositions, rowPositions } = computeGridPositions(grid);
  const totalCols = colPositions.length - 1;
  const totalRows = rowPositions.length - 1;
  const scope = args.location?.scope;
  const validCells = new Set();
  const sitedCells = host._cellsWithSites();
  for (const key of cellsInScope(sourceCell, scope, totalCols, totalRows)) {
    const [colStr, rowStr] = key.split(',');
    const col = parseInt(colStr, 10);
    const row = parseInt(rowStr, 10);
    // Exclude the source's own cell EXCEPT when the scope is 'here'
    // (Necromancer summons skeletons on its own cell). Midland Army
    // uses scope: 'adjacent' which excludes naturally, but the
    // belt-and-suspenders check stays for nearby / global.
    if (scope !== 'here' && col === sourceCell.col && row === sourceCell.row) continue;
    // Tokens without Voidwalk can't spawn in void cells. Site
    // required. Token catalog doesn't carry printed keywords today
    // so the gate is conservative for every token.
    if (!sitedCells.has(`${col},${row}`)) continue;
    validCells.add(`${col},${row}`);
  }

  const count = args.count || Math.max(1, validCells.size);
  if (validCells.size === 0) {
    host.logAction('{source}: no valid sites for {token} tokens', { source: cardLogLabel(sourceCard, msg('Source')), token: tokenName });
    return;
  }

  const wantRotated = controller === 'p2';
  const sourceLabel = sourceCard.name || msg('Source');
  const queue = [];
  for (let i = 0; i < count; i++) {
    queue.push({ tokenRow, controllerRotated: wantRotated, validCells, sourceLabel });
  }
  host._startSummonPlacements(queue, tokenName);
}

// v1.88. Set up the placement queue and surface the halos +
// cursor preview. Multiple summon ops in one entry are fine: a
// second _startSummonPlacements call replaces the queue (rare; today
// only one summon op runs per effect resolution).
export function startSummonPlacements(host, queue, tokenName) {
  if (!queue || queue.length === 0) return;
  const head = queue[0];
  const printing = head.tokenRow?.printings?.[0];
  const imageUrl = head.pileEntry?.imageUrl
    || printing?.image_url
    || head.tokenRow?.imageUrl
    || null;
  host.setState({
    pendingSummonPlacement: {
      queue,
      imageUrl,
      tokenName,
    },
  });
  if (queue.length > 1) {
    toast.message(t('Click to place {n} {name} tokens (ESC to skip).', { n: queue.length, name: tokenName }));
  } else {
    toast.message(t('Click to place {name} (ESC to skip).', { name: tokenName }));
  }
}

// v1.88. Click handler while pendingSummonPlacement is set. Routes
// the click to a cell, validates against the head entry's validCells,
// spawns + dequeues. Returns true if the click was consumed.
export function tryResolveSummonPlacement(host, event, cellOverride = null) {
  const pending = host.state.pendingSummonPlacement;
  if (!pending || pending.queue.length === 0) return false;
  // cellOverride lets a non-pointer caller (the e2e bridge) drive the
  // queue by grid cell instead of a raycast. The live click path leaves
  // it null and resolves the cell from the pointer event.
  let cell = cellOverride;
  let point = null;
  if (!cell) {
    point = host.scene?.raycastTablePoint(event);
    if (!point) return false;
    cell = host._cellAtWorld(point.x, point.z);
  }
  if (!cell) return false;
  const head = pending.queue[0];
  const key = `${cell.col},${cell.row}`;
  if (!head.unrestricted && !head.validCells.has(key)) {
    // Off-halo click. Keep targeting active so the player can retry.
    return false;
  }
  const grid = getGameGrid(host.state.spawnConfig);
  const geo = grid && sampleCellGeometry(grid, cell.col, cell.row);
  if (!geo) return false;
  const cx = (geo.tl.x + geo.tr.x + geo.bl.x + geo.br.x) / 4;
  const cz = (geo.tl.z + geo.tr.z + geo.bl.z + geo.br.z) / 4;
  if (head.customSpawn) {
    // Caller owns the actual spawn (e.g. Geomancer's Rubble, which is
    // a Site and needs siteSnapPoint anchoring rather than cell-center
    // token placement). The queue layer only handles UX. Preview
    // image, halos, and dequeue.
    head.customSpawn(cell);
    playSound('cardPlace');
  } else if (head.pileEntry && host._authZonesEnabled?.() && !head.pileEntry.isSite
      && head.pileEntry.cardId && host._getFullCard(head.pileEntry.cardId)) {
    // Manual Token Browser spawn, auth path: the server mints the token at
    // the picked cell so it is real MatchState. Every client renders it
    // from the diff. Cosmetic custom tokens (not in sorcery-cards.json)
    // fall through to the legacy local clone below.
    // The raw click point rides the intent (free card placement): the server
    // persists it as the token's pos so a snap-off client renders the token
    // exactly where it was placed. The e2e cellOverride path has no pointer
    // point; the token falls back to the cell cascade.
    host._sendMatchIntent(spawnTokenIntent({
      cardId: head.pileEntry.cardId,
      seat: head.controllerRotated ? 'p2' : 'p1',
      col: cell.col,
      row: cell.row,
      ...(point ? { x: point.x, z: point.z } : {}),
    }));
    playSound('cardPlace');
    host.logAction('Spawned {token} at [{cell}]', { token: pending.tokenName, cell: getCellNumber(cell) });
  } else if (head.pileEntry) {
    // Manual Token Browser spawn. Clone the pile entry with a fresh
    // id and the picked cell position, then route through addCardToTable
    // so it shares the same drag / ownership / banish flow as any other
    // card on the field. Covers both Sorcery-catalog tokens and
    // cosmetic custom tokens (which aren't in sorcery-cards.json).
    const clone = {
      ...head.pileEntry,
      id: `${head.pileEntry.id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      x: cx,
      y: 0,
      z: cz,
      rotated: head.controllerRotated,
    };
    host.addCardToTable(clone, true, { logVerb: k('Spawned') });
    playSound('cardPlace');
    host.logAction('Spawned {token} at [{cell}]', { token: pending.tokenName, cell: getCellNumber(cell) });
  } else {
    host._spawnTokenAtPoint(head.tokenRow, head.controllerRotated, { x: cx, z: cz });
    host.logAction('{source}: summoned {token} at [{cell}]', { source: head.sourceLabel, token: pending.tokenName, cell: getCellNumber(cell) });
  }
  const restQueue = pending.queue.slice(1);
  if (restQueue.length === 0) {
    host.setState({ pendingSummonPlacement: null });
  } else {
    host.setState({
      pendingSummonPlacement: { ...pending, queue: restQueue },
    });
  }
  return true;
}

// v1.88. ESC cancels every remaining placement. Per codex, summon
// effects on triggered abilities are typically "you may" so canceling
// mid-queue is fine; the rest just don't spawn.
export function cancelSummonPlacements(host) {
  const pending = host.state.pendingSummonPlacement;
  if (!pending) return;
  const remaining = pending.queue.length;
  if (remaining === 1) host.logAction('{token}: 1 placement skipped', { token: pending.tokenName });
  else host.logAction('{token}: {n} placements skipped', { token: pending.tokenName, n: remaining });
  host.setState({ pendingSummonPlacement: null });
}

// Right-click "Place on field" from a pile-search dialog (Spellbook,
// Atlas, Cemetery, Collection, Banished). Closes the dialog and enters
// the same click-to-place flow Necromancer / Geomancer use: the cursor
// shows a preview of the card, every cell is a legal target
// (`unrestricted: true`), and ESC cancels without touching the pile.
// The customSpawn callback handles the actual pile→table move so we
// get the cell-snapped position rather than dumping the card at world
// origin like the old spawnCardFromPile path.
export function startPileCardPlacement(host, sourcePile, cardInstance) {
  if (!sourcePile || !cardInstance) return;
  // Token piles already have their own click-to-place flow via
  // _startManualTokenSpawn. The right-click menu hides Place on field
  // for them, but guard anyway.
  if (sourcePile.infinite) return;
  // Pre-flight: if the card has already been pulled out of the pile
  // (stale reference / race), there's nothing to place.
  if (!sourcePile.cards.some((c) => c.id === cardInstance.id)) return;

  const cardName = cardInstance.name || 'card';
  const sourcePileId = sourcePile.id;
  host.setState({ searchPile: null, searchQuery: '' });

  host._startSummonPlacements(
    [{
      unrestricted: true,
      // pileEntry here is only consumed by _startSummonPlacements to
      // resolve the preview image. CustomSpawn owns the real spawn.
      pileEntry: { imageUrl: cardInstance.imageUrl },
      sourceLabel: sourcePile.name,
      customSpawn: (cell) => {
        // Re-resolve the pile by id at spawn time: the original
        // sourcePile reference may have been replaced by a pile:sync
        // since the dialog closed. Bail if the pile or card has moved.
        const livePile = host.state.gameState.piles.find((p) => p.id === sourcePileId);
        if (!livePile) return;
        const idx = livePile.cards.findIndex((c) => c.id === cardInstance.id);
        if (idx === -1) return;
        const removed = livePile.cards[idx];
        livePile.cards.splice(idx, 1);

        // Authoritative ownership: the pile's rotated flag wins in
        // sandbox (a card inside cemetery-p1 belongs to P1), matching
        // takeCardFromPile / spawnCardFromPile semantics.
        if (host.props.isCasualSandbox) {
          removed.rotated = !!livePile.rotated;
        }

        const grid = getGameGrid(host.state.spawnConfig);
        const geo = grid && sampleCellGeometry(grid, cell.col, cell.row);
        if (geo) {
          removed.x = (geo.tl.x + geo.tr.x + geo.bl.x + geo.br.x) / 4;
          removed.z = (geo.tl.z + geo.tr.z + geo.bl.z + geo.br.z) / 4;
          removed.y = 0;
        }
        host.addCardToTable(removed, true, { logVerb: k('Placed') });
        // Auth: record the pile → board move so matchState's zone flips to
        // 'play'. Without it the server keeps the card in its pile zone: the
        // opponent's board (diff-rendered, zone 'play' only) never shows the
        // card, and a reload drops it locally too.
        if (host._authZonesEnabled?.()) host._emitMoveToZone(removed.id, 'play');
        host.updatePileMeshes();
        host.logAction('Placed {card} from {pile} at [{cell}]', { card: cardLogLabel(removed, msg('a card')), pile: livePile.name, cell: getCellNumber(cell) });
      },
    }],
    cardName,
  );
}

// Manual Token Browser spawn (B hotkey → pile dialog → click a tile).
// Closes the dialog and enters the same click-to-place flow that
// SUMMON_TOKEN_AT_LOCATIONS uses, but with `unrestricted: true` so the
// player can drop the token on any cell. ESC cancels.
export function startManualTokenSpawn(host, pileEntry) {
  if (!pileEntry) return;
  const isLocalP2 = host.props.isCasualSandbox
    ? host.state.activePlayerNum === 2
    : !host.state.isHost;
  const tokenName = pileEntry.name
    || host._getFullCard(pileEntry.cardId)?.name
    || 'token';
  host.setState({ searchPile: null, searchQuery: '' });
  host._startSummonPlacements(
    [{
      unrestricted: true,
      pileEntry,
      controllerRotated: isLocalP2,
      sourceLabel: 'Token Browser',
    }],
    tokenName,
  );
}

// v1.46. True if the controller has at least one site card in
// their displayed hand. Used by _isMandatorySitePlayPending; if
// they have no sites, the codex "may and must" rule doesn't bite.
export function hasSiteInHand(host, controller) {
  const handKey = controller === 'p1' ? 'handCards' : 'handCards2';
  const hand = host.state[handKey] || [];
  return hand.some((c) => {
    if (c.isSite) return true;
    if (c.type === 'Site') return true;
    const fc = host._getFullCard(c.cardId);
    return fc?.type === 'Site';
  });
}

// Find the controller's Avatar instance (the cardInstance, not
// just the mesh). Returns null if no Avatar is in play.
export function findAvatarCard(host, controllerRotated) {
  for (const [, mesh] of host.meshes) {
    const c = mesh?.userData?.cardInstance;
    if (!c || c.type !== 'Avatar') continue;
    if (!!c.rotated !== !!controllerRotated) continue;
    return c;
  }
  return null;
}

// v1.46. Codex "Avatar in Void" mandatory action: if your Avatar
// is in the void and you have a site in hand, you MUST play it
// under the avatar before doing other things in your main phase.
// We enforce this by blocking non-Site fresh plays while it holds.
// Per codex, if you can't legally take the mandatory action you
// aren't forced. So a tapped/disabled Avatar releases the block.
export function isMandatorySitePlayPending(host, controller) {
  const rotated = controller === 'p2';
  if (!isAvatarInVoid(rotated, host.meshes, host._cellAtWorld, host._getFullCard)) return false;
  if (!host._hasSiteInHand(controller)) return false;
  const avatar = host._findAvatarCard(rotated);
  if (!avatar || avatar.tapped || isDisabled(avatar)) return false;
  return true;
}

// v1.84. Codex Mandatory Actions: "You must resolve mandatory
// actions during your main phase before doing anything else." Today
// only the Avatar-in-void site-play rule is wired; future cards
// (Mask of Mayhem, The Green Knight, etc.) hook into this helper so
// every gated input checks one place instead of duplicating the
// check. Returns a short reason string if blocked, or null.
export function anyMandatoryActionPending(host, controller) {
  if (host._isMandatorySitePlayPending(controller)) {
    return k('Play a site under your Avatar first.');
  }
  return null;
}

// True when an UNRELATED effect is mid-resolution on the storyline.
// The cast pipeline itself queues the spell's own target / cell / pick
// prompts (routeSpellPlay / castSpellWithCaster), so a naive
// pendingPrompts.length check would self-block. We exclude every
// pending entry whose source card IS the card being played: storyline
// prompts reference their source via `cardInstance.id`, while
// pendingTarget / pendingCellTarget reference it via `sourceCard.id`.
function storylineBusyForCard(host, card) {
  const selfId = card.id;
  const prompts = host.state.pendingPrompts || [];
  const otherPrompt = prompts.some(
    (p) => (p.cardInstance?.id ?? p.sourceCard?.id) !== selfId,
  );
  if (otherPrompt) return true;
  const pt = host.state.pendingTarget;
  if (pt && pt.sourceCard?.id !== selfId) return true;
  const pct = host.state.pendingCellTarget;
  if (pct && pct.sourceCard?.id !== selfId) return true;
  return false;
}

// Turn + cost check for a fresh play. Returns { ok: true } or
// { ok: false, reason, message } where message is a player-ready
// toast string with no em-dashes (per feedback_ui_copy_style.md).
export function canPlayFromHand(host, card) {
  const controller = card.rotated ? 'p2' : 'p1';
  if (host.state.currentTurn !== controller) {
    return {
      ok: false,
      reason: 'wrong_turn',
      message: `${card.name || 'Card'} can't be played on your opponent's turn.`,
    };
  }
  // Owner-locked draw ("Only It Can Cast." Omphalos / Morgana /
  // Gabriel): a card tagged `_castableOnlyBy` may only be cast while its
  // owner permanent is on the board AND still able to cast spells. A
  // silenced / disabled / transformed minion owner has lost the
  // Spellcaster grant and can no longer cast; an artifact Omphalos casts
  // inherently and stays able (ownerCanCastLocked reads the live owner
  // instance). applyFreshPlayGating hard-blocks the `owner_locked`
  // reason rather than treating it as advisory.
  const lock = checkCastLock(
    card,
    (id) => host.meshes.has(id),
    null,
    (id) => ownerCanCastLocked(host.meshes.get(id)?.userData?.cardInstance),
  );
  if (!lock.ok) return lock;
  // A held Minion cast as a spell still obeys its owner's element
  // restriction: an element-restricted Omphalos owner may only cast it if
  // it requires one of the owner's elements. Morgana (full spellcaster) and
  // a Wiccan Tools waiver this turn are unrestricted.
  const ownerLockId = card._castableOnlyBy;
  if (ownerLockId) {
    const owner = host.meshes.get(ownerLockId)?.userData?.cardInstance;
    if (owner && !owner._thresholdWaiverThisTurn && !owner._spellcasterNoThresholdThisTurn
        && !ownerElementAllowsCast(host._getFullCard(owner.cardId), host._getFullCard(card.cardId))) {
      return {
        ok: false,
        reason: 'owner_locked',
        message: `${card.name || 'This spell'} can only be cast by its owner, which cannot cast that element.`,
      };
    }
  }
  // v1.46. Mandatory site-under-Avatar blocks every other play.
  // Sites themselves are allowed through (the placement check
  // below pins them to the avatar's cell).
  // v1.84. Sites bypass this gate because playing one IS the
  // mandatory action; any other card type falls through to the
  // unified mandatory check.
  if (!card.isSite) {
    const blocked = host._anyMandatoryActionPending(controller);
    if (blocked) return { ok: false, reason: 'mandatory_action_first', message: blocked };
    // Codex: a spell may only be cast while the storyline is empty.
    // ADVISORY (applyFreshPlayGating logs, doesn't block). Only the
    // owner-lock reason is a hard block. We refuse only when an
    // unrelated effect is mid-resolution; the spell's own follow-up
    // prompts (which the cast pipeline queues) never count.
    if (storylineBusyForCard(host, card)) {
      return {
        ok: false,
        reason: 'storyline_busy',
        message: `${card.name || 'Spell'} can't be cast while another effect is resolving.`,
      };
    }
  }
  // Codex "one site per turn." Naturally bounded by the Avatar's
  // single Tap → play-site ability. The playable-glow highlight has
  // to mirror that ceiling: once the Avatar is tapped AND there's
  // no card-effect grant AND no mandatory-under-Avatar trigger, no
  // further sites can be played this turn, so they should stop
  // glowing in hand instead of advertising an action the placement
  // gate will refuse.
  if (card.isSite) {
    const controllerRotated = !!card.rotated;
    const hasGrant = siteGrantInfo(host, controller).count > 0;
    const avatar = host._findAvatarCard(controllerRotated);
    const avatarUntapped = !!avatar && !avatar.tapped;
    let mandatory = false;
    const grid = getGameGrid(host.state.spawnConfig);
    if (grid) {
      const { colPositions, rowPositions } = computeGridPositions(grid);
      const bannedCells = bannedSiteCellsFor(host);
      const legal = getLegalSitePlacement(
        controllerRotated, host._meshesExcludingStagedDrag(), host._cellAtWorld, host._getFullCard,
        colPositions.length - 1, rowPositions.length - 1, bannedCells,
      );
      mandatory = !!legal?.mandatory;
    }
    if (!mandatory && !hasGrant && !avatarUntapped) {
      return {
        ok: false,
        reason: 'no_site_play',
        message: `${card.name || 'Site'} can't be played. Your Avatar is tapped and no effect granted another site this turn.`,
      };
    }
  }
  const fullCard = host._getFullCard(card.cardId);
  // The Apex of Babel free-cast window: while open for the controller,
  // any spell (non-Site) may be played regardless of mana. The cost is
  // waived in payCastingCost. Skip the affordability gate entirely.
  const fcType = fullCard?.type ?? fullCard?.guardian?.type ?? card.type;
  if (fcType !== 'Site' && host._isFreeCastActive?.(controller)) {
    return { ok: true };
  }
  // v1.89 / v1.99. Apply active cost modifiers (Tax Collector,
  // Templar, etc.). Passing fullCard lets filtered modifiers gate
  // on the candidate's type / subtype.
  const costAdjustment = host.effectLedger.costModifierTotal(controller, fullCard);
  const afford = getAffordability(fullCard, host.playerResources.snapshot(controller), { costAdjustment });
  if (!afford.ok) {
    // Dragonlord's Lair: threshold-only refusals are forgiven when an
    // in-play waiver matches the candidate ("Dragons cast to this
    // site require no threshold"). The placement pipeline then
    // restricts the legal spawn set to the waiver source's cell.
    const thresholdOnly = afford.refusals.length > 0
      && afford.refusals.every((r) => r.kind === 'threshold');
    if (thresholdOnly) {
      const waivers = collectThresholdWaivers(host.meshes, host._cellAtWorld, host._getFullCard);
      if (thresholdWaiverCellsFor(waivers, fullCard).size > 0) return { ok: true };
    }
    const name = card.name || fullCard?.name || 'Card';
    return {
      ok: false,
      reason: 'insufficient',
      refusals: afford.refusals,
      message: `${name} can't be played. Need ${formatRefusalsSentence(afford.refusals)}.`,
    };
  }
  return { ok: true };
}

// v1.46 / v1.47. Site-placement gating. Three paths to legal:
//
//   • Grant active (from Avatar tap OR card-effect grant): drop
//     must land adjacent to a controlled non-Rubble site OR under
//     the Avatar. Consumes one grant.
//   • Mandatory Avatar-in-void: drop must be under the Avatar.
//     Auto-taps the Avatar (its ability is the licence. Codex
//     "may and must use their ability").
//   • UX shortcut: no grant exists AND the Avatar is untapped →
//     auto-tap the Avatar to grant the play implicitly, then
//     check the placement as normal. This collapses the
//     double-click-menu-choice-drag flow into a single drag for
//     the common case. Card-effect grants take precedence so
//     they're not consumed accidentally by the shortcut.
//
// Free placement (no grant, Avatar tapped, not in void) is
// REJECTED with an explanatory toast.
export function canPlaceSiteAt(host, siteCard, member) {
  const controllerRotated = !!siteCard.rotated;
  const controller = controllerRotated ? 'p2' : 'p1';
  const toCell = host._cellAtWorld(member.mesh.position.x, member.mesh.position.z);
  if (!toCell) {
    return { ok: false, message: `${siteCard.name || 'Site'} can't be placed off-grid.` };
  }
  const grid = getGameGrid(host.state.spawnConfig);
  const { colPositions, rowPositions } = grid ? computeGridPositions(grid) : { colPositions: [], rowPositions: [] };
  const totalCols = colPositions.length - 1;
  const totalRows = rowPositions.length - 1;
  const bannedCells = bannedSiteCellsFor(host);
  const legal = getLegalSitePlacement(controllerRotated, host._meshesExcludingStagedDrag(), host._cellAtWorld, host._getFullCard, totalCols, totalRows, bannedCells);
  const classification = classifyCellForSite(legal, toCell.col, toCell.row);

  // Mandatory under-Avatar mode. The Avatar's ability IS the
  // licence; using it taps the Avatar (auto-tap, no grant change).
  if (legal.mandatory) {
    if (classification !== 'under_avatar') {
      return {
        ok: false,
        message: `Must place site under your Avatar (currently in the void).`,
      };
    }
    const avatar = host._findAvatarCard(controllerRotated);
    return { ok: true, consumeGrant: false, autoTapAvatarId: avatar?.id || null };
  }

  if (classification === 'illegal') {
    return {
      ok: false,
      message: `${siteCard.name || 'Site'} must be played adjacent to a site you control.`,
    };
  }

  // Card-effect grant precedence: if one exists, consume it
  // without touching the Avatar. This keeps Druid/Mystic/etc.
  // ability flows working as authored. Auth reads the server allowance
  // from the mirror (the server consumes it at the site-cast commit).
  const grant = siteGrantInfo(host, controller);
  if (grant.count > 0) {
    // Landmass / Overflow narrow the grant to one site kind. A grant
    // carrying a land/water restriction refuses a mismatched site.
    const restriction = grant.restriction;
    if (!siteMatchesRestriction(siteCard, host._getFullCard(siteCard.cardId), restriction)) {
      return {
        ok: false,
        message: `${siteCard.name || 'Site'} can't be played. You may only play a ${restriction} site this turn.`,
      };
    }
    return { ok: true, consumeGrant: true, autoTapAvatarId: null };
  }

  // No grant. Try the UX shortcut: auto-tap the Avatar.
  const avatar = host._findAvatarCard(controllerRotated);
  if (!avatar) {
    return {
      ok: false,
      message: `No Avatar in play to tap for site placement.`,
    };
  }
  if (avatar.tapped) {
    return {
      ok: false,
      message: `Your Avatar is tapped. Can't play another site this turn.`,
    };
  }
  if (isDisabled(avatar)) {
    return {
      ok: false,
      message: `Your Avatar is disabled. Can't tap for site placement.`,
    };
  }
  return { ok: true, consumeGrant: false, autoTapAvatarId: avatar.id };
}
